<template>
  <div ref="historyContainerRef" class="early-settlement-history">
    <div :class="['list-area box', { 'list-area--has-new-row': hasNewRow }]">
      <div class="title-row">
        <p class="table-title">
          Erken Ödeme Talep Geçmişim
        </p>
      </div>
      <div class="controls-row">
        <div class="dropdown-container">
          <Dropdown
            class="status-dropdown"
            :class="{ active: statusDropdownState }"
            :dropdownState="statusDropdownState"
            @dropdownState="statusDropdownState = $event"
          >
            <template #dropdown-click>
              <app-button
                class="dropdown-btn"
                title="Durum"
                :secondary-magenta="true"
                :sm="true"
              >
                <template #appendIcon>
                  <Icons
                    name="BottomArrows"
                    :class="['dropdown-btn-arrow', statusDropdownState && 'icon-rotate']"
                    :color="iconColor"
                  />
                </template>
              </app-button>
            </template>
            <template #dropdown-content>
              <div class="dropdown-menu">
                <button
                  v-for="(item, index) in batchActionProps"
                  :key="index"
                  type="button"
                  class="dropdown-menu-item"
                  @click="handleStatusSelect(index)"
                >
                  {{ item.item }}
                </button>
              </div>
            </template>
          </Dropdown>
          <Dropdown
            class="sort-dropdown"
            :class="{ active: sortDropdownState }"
            :dropdownState="sortDropdownState"
            @dropdownState="sortDropdownState = $event"
          >
            <template #dropdown-click>
              <app-button
                class="dropdown-btn"
                title="Sırala"
                :secondary-magenta="true"
                :sm="true"
              >
                <template #prependIcon>
                  <Icons
                    name="Sort"
                    :color="iconColor"
                    size="sm"
                    class="sort-icon"
                  />
                </template>
                <template #appendIcon>
                  <Icons
                    name="BottomArrows"
                    :class="['dropdown-btn-arrow', sortDropdownState && 'icon-rotate']"
                    :color="iconColor"
                  />
                </template>
              </app-button>
            </template>
            <template #dropdown-content>
              <div class="dropdown-menu">
                <button
                  v-for="(item, index) in dateSortOptions"
                  :key="index"
                  type="button"
                  class="dropdown-menu-item"
                  @click="handleSortSelect(index)"
                >
                  {{ item.item }}
                </button>
              </div>
            </template>
          </Dropdown>
        </div>
        <div v-if="!isEmpty" class="pagination-row">
          <Pagination
            :page-count="totalPages"
            :page-range="5"
            :click-handler="handlePageChange"
            :first-last-button="true"
            container-class="button-group"
            prev-link-class="border left rounded p-1"
            page-link-class="border rounded p-1"
            next-link-class="border right rounded p-1"
            :margin-pages="0"
            :value="currentPage - 1"
          />
          <div class="per-page-wrapper">
            <span class="per-page-text">Her Sayfada</span>
            <SimpleSelect
              :simple-select-items="pageSizes"
              :selected-key="pageSize"
              class="simpleSelect"
              @btn-item-click="handlePageSizeChange(Number($event))"
            />
          </div>
        </div>
      </div>
      <DataTable
        v-if="!isEmpty"
        ref="historyTableRef"
        :payload="payloadData"
        :columns="columns"
        :rows="tableData"
        :showPagination="false"
      />
      <div v-if="!isEmpty" class="external-pagination">
        <Pagination
          :page-count="totalPages"
          :page-range="5"
          :click-handler="handlePageChange"
          :first-last-button="true"
          container-class="button-group"
          prev-link-class="border left rounded p-1"
          page-link-class="border rounded p-1"
          next-link-class="border right rounded p-1"
          :margin-pages="0"
          :value="currentPage - 1"
        />
        <div class="per-page-wrapper">
          <span class="per-page-text">Her Sayfada</span>
          <SimpleSelect
            :simple-select-items="pageSizes"
            :selected-key="pageSize"
            open-menu-position="top-left"
            class="simpleSelect"
            @btn-item-click="handlePageSizeChange(Number($event))"
          />
        </div>
      </div>
      <div v-if="isEmpty" :class="['empty-table-container', { 'empty-table-container--filtered': isFiltered }]">
        <img
          class="empty-table-img"
          :src="isFiltered ? require('@/assets/images/empty-search.png') : require('@/assets/images/icons/no-receipts-found.svg')"
          alt="Empty Table"
        />
        <span>{{ isFiltered ? 'Seçtiğiniz kriterlere uygun talep bulunmuyor.' : 'Henüz bir talep oluşturmadınız.' }}</span>
      </div>
    </div>
    <CancelRequestModal
      ref="cancelRequestModalRef"
      :requestNumber="selectedRequestNumber"
      @confirm="handleCancelRequest"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick, computed } from 'vue';
import DataTable from '@/components/Table/DataTable';
import Pagination from '@/components/Table/Pagination';
import { earlySettlementHistoryColumns } from '../services/columnService';
import { Dropdown, AppButton, Icons, SimpleSelect } from '@n11/so-ui-components';
import CancelRequestModal from './modals/CancelRequestModal.vue';
import useEarlySettlementHistory from '../services/historyService';

export default {
  name: "EarlySettlementHistory",
  components: {
    DataTable,
    Pagination,
    Dropdown,
    AppButton,
    Icons,
    SimpleSelect,
    CancelRequestModal,
  },
  props: {
    externalRow: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const historyTableRef = ref();
    const historyContainerRef = ref(null);
    const cancelRequestModalRef = ref(null);
    const selectedRequestNumber = ref('');
    const statusDropdownState = ref(false);
    const sortDropdownState = ref(false);
    const iconColor = '#1C1C1E';

    const currentPage = ref(1);
    const pageSize = ref(10);
    const pageSizes = { 10: '10 Talep', 25: '25 Talep', 50: '50 Talep', 100: '100 Talep' };

    const payloadData = ref({
      tabParam: 'early_settlement_history',
      data: [],
      useLocalData: true,
    });
    const isEmpty = ref(true);
    const hasNewRow = ref(false);
    const tableData = ref([]);
    const selectedStatusIndex = ref(0);
    const selectedSortIndex = ref(0);

    const batchActionProps = [
      { item: "Tümü", apiValue: null },
      { item: "Talebiniz onaylandı", apiValue: 'APPROVED' },
      { item: "Erken ödeme yapıldı", apiValue: 'PAID' },
      { item: "Talebiniz onaylanmadı", apiValue: 'REJECTED' },
    ];

    const dateSortOptions = [
      { item: "Talep oluşturma tarihi en yakın", apiValue: 'REQUEST_DATE_DESC' },
      { item: "Talep oluşturma tarihi en uzak", apiValue: 'REQUEST_DATE_ASC' },
      { item: "Erken ödeme tarihi en yakın", apiValue: 'EARLY_PAYMENT_DATE_DESC' },
      { item: "Erken ödeme tarihi en uzak", apiValue: 'EARLY_PAYMENT_DATE_ASC' },
    ];

    const handleStatusSelect = (index) => {
      selectedStatusIndex.value = index;
      statusDropdownState.value = false;
      currentPage.value = 1;
    };

    const handleSortSelect = (index) => {
      selectedSortIndex.value = index;
      sortDropdownState.value = false;
      currentPage.value = 1;
    };

    const handlePageChange = (pageIndex) => {
      currentPage.value = Math.min(pageIndex + 1, totalPages.value);
    };

    const handlePageSizeChange = (size) => {
      pageSize.value = size;
      currentPage.value = 1;
    };

    const openCancelRequestModal = (requestNumber) => {
      selectedRequestNumber.value = requestNumber;
      if (cancelRequestModalRef.value) {
        cancelRequestModalRef.value.openModal();
      }
    };

    const handleCancelRequest = (requestNumber) => {
      console.log('Erken ödeme talebinden vazgeçildi:', requestNumber);
    };

    const columns = ref(earlySettlementHistoryColumns(openCancelRequestModal));

    const {
      data: historyData,
      isLoading,
      totalPages,
      totalItems,
      fetchData,
    } = useEarlySettlementHistory();

    const mapStatus = (statusText) => {
      const map = {
        'Talebiniz iletildi': 'Pending',
        'Talebiniz onaylandı': 'Approved',
        'Erken ödeme yapıldı': 'EarlyPaid',
        'Talebiniz onaylanmadı': 'Rejected',
        'Talepten vazgeçtiniz': 'Cancelled',
      };
      return map[statusText] || 'Pending';
    };

    const mapHistoryResponseToTableData = (items) => {
      if (!Array.isArray(items)) return [];
      return items.map((item, index) => ({
        id: index + 1,
        requestNumber: item.requestNumber,
        requestDate: item.requestDate,
        earlySettlementDate: item.earlyPaymentDate,
        earlySettlementAmount: item.earlyPaymentAmount?.startsWith('₺')
          ? item.earlyPaymentAmount
          : `₺${item.earlyPaymentAmount}`,
        description: item.description,
        status: mapStatus(item.status),
      }));
    };

    const isFiltered = computed(() => selectedStatusIndex.value > 0);

    const loadData = () => {
      const status = batchActionProps[selectedStatusIndex.value]?.apiValue || null;
      const sort = dateSortOptions[selectedSortIndex.value]?.apiValue || null;
      fetchData({
        page: currentPage.value,
        itemsPerPage: pageSize.value,
        status,
        sort,
      });
    };

    watch(
      historyData,
      (items) => {
        const mapped = mapHistoryResponseToTableData(items || []);
        payloadData.value = { ...payloadData.value, data: mapped };
        tableData.value = mapped;
        isEmpty.value = mapped.length === 0;
      },
      { deep: true }
    );

    watch(
      [selectedStatusIndex, selectedSortIndex, currentPage, pageSize],
      () => {
        hasNewRow.value = false;
        loadData();
      },
    );

    onMounted(() => { loadData(); });

    const prependRow = async (item) => {
      if (!item) return;
      const mappedItems = mapHistoryResponseToTableData([item]);
      if (!mappedItems.length) return;
      const newRow = {
        ...mappedItems[0],
        id: (tableData.value[0]?.id || 0) + 1,
      };
      tableData.value = [newRow, ...tableData.value];
      payloadData.value = { ...payloadData.value, data: tableData.value };
      isEmpty.value = false;
      hasNewRow.value = true;
      await nextTick();
      if (historyContainerRef.value) {
        historyContainerRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    watch(
      () => props.externalRow,
      (val) => { if (val) prependRow(val); },
      { deep: true }
    );

    return {
      historyTableRef,
      historyContainerRef,
      cancelRequestModalRef,
      selectedRequestNumber,
      payloadData,
      isEmpty,
      isFiltered,
      isLoading,
      hasNewRow,
      columns,
      tableData,
      batchActionProps,
      dateSortOptions,
      statusDropdownState,
      sortDropdownState,
      handleStatusSelect,
      handleSortSelect,
      handlePageChange,
      handlePageSizeChange,
      handleCancelRequest,
      iconColor,
      prependRow,
      currentPage,
      pageSize,
      pageSizes,
      totalPages,
      totalItems,
    };
  },
};

</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.early-settlement-history {
  position: relative;
  width: 100%;
  padding: 24px;
  background: $colorN00;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 100px;
  
  &-title { 
    color: $colorN80;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: -0.05px;
  }
  
  .icon-preview {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid $colorN30;
    border-radius: 8px;
    background-color: $colorN00;

    .icon-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;

      p {
        font-size: 12px;
        color: $colorN80;
        margin: 0;
      }
    }
  }

  .title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
    margin-bottom: 16px;
  }

  .controls-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
  }

  .dropdown-container {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  .pagination-row {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .external-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    width: 100%;
    padding: 12px 0 0;
  }

  .per-page-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .per-page-text {
    font-size: 12px;
    color: $colorN60;
    white-space: nowrap;
  }

  .status-dropdown {
    width: 92px;

    &.active {
      .dropdown-btn {
        border: 2px solid $colorM50;
        background-color: $colorM05;
        outline: 3px solid $colorD05;
      }
    }

    .dropdown-btn {
      width: 100%;
      justify-content: flex-start;
      text-align: left;
      padding: 10px;
      border: 1px solid $colorN30;
      background-color: $colorN00;
    }

    .dropdown-btn-arrow {
      position: relative;
      top: 2px;
      transition: transform 0.3s;
      margin-left: auto;

      &.icon-rotate {
        transform: rotate(-180deg);
      }
    }

    .dropdown-menu {
      width: 200px;
      display: flex;
      flex-direction: column;
      height: auto;
      border-radius: 6px;
      border: 1px solid $colorN50;
      box-shadow: 0 0 0 3px rgba($colorN50, 0.3);
      background-color: $colorN00;

      &-item {
        display: flex;
        justify-content: flex-start;
        text-align: left;
        width: 100%;
        padding: 14px 18px;
        color: $colorN80;
        font-size: 12px;
        font-weight: 600;
        transition: 0.3s;
        border: none;
        background: none;
        cursor: pointer;

        &:hover {
          background-color: $colorD05;
        }

        &:not(:last-child) {
          border-bottom: 0.5px solid $colorD30;
        }
      }
    }
  }

  .sort-dropdown {
    width: 92px;

    &.active {
      .dropdown-btn {
        border: 2px solid $colorM50;
        background-color: $colorM05;
        outline: 3px solid $colorD05;
      }
    }

    .dropdown-btn {
      width: 100%;
      justify-content: flex-start;
      text-align: left;
      padding: 10px;
      border: 1px solid $colorN30;
      background-color: $colorN00;
    }

    .dropdown-btn-arrow {
      position: relative;
      top: 2px;
      transition: transform 0.3s;
      margin-left: auto;

      &.icon-rotate {
        transform: rotate(-180deg);
      }
    }

    .dropdown-menu {
      width: 220px;
      display: flex;
      flex-direction: column;
      height: auto;
      border-radius: 6px;
      border: 1px solid $colorN50;
      box-shadow: 0 0 0 3px rgba($colorN50, 0.3);
      background-color: $colorN00;

      &-item {
        display: flex;
        justify-content: flex-start;
        text-align: left;
        width: 100%;
        padding: 14px 18px;
        color: $colorN80;
        font-size: 12px;
        font-weight: 600;
        transition: 0.3s;
        border: none;
        background: none;
        cursor: pointer;

        &:hover {
          background-color: $colorD05;
        }

        &:not(:last-child) {
          border-bottom: 0.5px solid $colorD30;
        }
      }
    }
  }

  .empty-table-container {
    height: 236px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    border: 1px solid $colorN50;
    border-radius: 8px;
    padding: 24px 0px;
    margin-top: 16px;
    span {
      font-size: 20px;  
      font-weight: 600;
      line-height: 26px;
      letter-spacing: -0.2px;
      color: $colorN80;
    }
  }

  .empty-table-img {
    width: 120px;
    height: 120px;
  }
}

.list-area {
  width: 100%;
  display: flex;
  flex-direction: column;
  //gap: 16px;

  :deep(.advanced-table tbody tr td) {
    height: 56px;
  }

  :deep(.advanced-table tbody tr td:nth-child(6) label) {
    font-weight: 400;
  }

  &.list-area--has-new-row :deep(.advanced-table tbody tr:first-child td) {
    background: #FFFBEC;
  }

  :deep(.advanced-table tbody tr td .btn) {
    width: 69px;
    height: 24px !important;
    margin: 8px 12px;
    background-color: $colorN40;
    color: $colorN00;
    font-size: 11px;
    font-weight: 600;
    line-height: 14px;
    border: none;
  }

  :deep(.cancel-request-btn) {
    max-width: fit-content;
    height: 24px;
    background-color: #FFF !important;
    color: #000 !important;
    border: 1px solid #000 !important;
  }
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: $colorN80;
  margin: 0;
}

// Early Settlement Status Styles - Sadece DataTable içindeki status-wrapper'ları hedefle
.list-area :deep(.status-wrapper) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
  text-align: center;
  min-width: 80px;
  transition: all 0.2s ease;

  .icon {
    width: 12px;
    height: 12px;

    svg {
      width: 12px !important;
      height: 12px !important;
    }
  }
}

.list-area :deep(.status-wrapper.yellow) {
  border-radius: 4px;
  border: 0.5px solid #D9D9D9;
  background: #FFFBEC;
  color: #A38712;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
}

.list-area :deep(.status-wrapper.green) {
  border-radius: 4px;
  border: 0.5px solid $colorN30;
  background: $colorS10;
  color: $colorS60;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
}

.list-area :deep(.status-wrapper.blue) {
  border-radius: 4px;
  border: 0.5px solid $colorN30;
  color: $colorB60;
  background: $colorB10;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
}

.list-area :deep(.status-wrapper.red) {
  border-radius: 4px;
  border: 0.5px solid $colorN30;
  color: $colorE60;
  background: $colorE10;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
}

.list-area :deep(.status-wrapper.gray) {
  border-radius: 4px;
  border: 0.5px solid $colorN30;
  color: $colorN60;
  background: $colorN10;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;

  .icon {
    width: 16px;
    height: 16px;

    svg {
      width: 16px !important;
      height: 16px !important;
    }
  }
}
</style>
